import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { Button, Icon } from "components/hds";
import { Spinner, Typography } from "components/ui";
import { BmsAppointment } from "hooks/useBmsAppointments";

// The whole BMS flow runs in Switzerland, so appointments are always shown in
// Zurich wall-clock time regardless of where the endcustomer's browser is.
// BMS sends ISO-8601 with an offset, which `new Date()` resolves to an
// absolute instant; this only decides how that instant is displayed.
const BMS_TIME_ZONE = "Europe/Zurich";

const parse = (value: string | null): Date | null => {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

// "Di, 29. Juli 2027, 15:30 – 17:30" when start and end fall on the same day,
// otherwise both sides in full. Repeating the date for a two-hour appointment
// is noise, but an appointment crossing midnight must still be unambiguous.
const formatRange = (
	appointment: BmsAppointment,
	locale: string,
): string | null => {
	const start = parse(appointment.appointmentStart);
	const end = parse(appointment.appointmentEnd);
	if (!start) return null;

	const day: Intl.DateTimeFormatOptions = {
		weekday: "short",
		year: "numeric",
		month: "long",
		day: "numeric",
		timeZone: BMS_TIME_ZONE,
	};
	const time: Intl.DateTimeFormatOptions = {
		hour: "2-digit",
		minute: "2-digit",
		timeZone: BMS_TIME_ZONE,
	};

	const startDay = start.toLocaleDateString(locale, day);
	const startTime = start.toLocaleTimeString(locale, time);
	if (!end) return `${startDay}, ${startTime}`;

	const sameDay =
		start.toLocaleDateString(locale, { timeZone: BMS_TIME_ZONE }) ===
		end.toLocaleDateString(locale, { timeZone: BMS_TIME_ZONE });

	return sameDay
		? `${startDay}, ${startTime} – ${end.toLocaleTimeString(locale, time)}`
		: `${startDay}, ${startTime} – ${end.toLocaleDateString(
				locale,
				day,
		  )}, ${end.toLocaleTimeString(locale, time)}`;
};

// A manage link leaves the configurator for BMS's own site, and the
// configurator is a single-page app holding an unsaved scene — so always a new
// tab, never this one. (The booking link is embedded instead; see onBook.)
const openInNewTab =(url: string | null) => {
	if (url) {
		window.open(url, "_blank", "noopener,noreferrer");
	}
};

interface BmsAppointmentsPanelProps {
	appointments: BmsAppointment[];
	// Calenso widget URL for booking a NEW appointment. Null when BMS has
	// never sent one for this endcustomer, in which case no button is offered
	// rather than a dead one.
	bookingLink: string | null;
	loaded: boolean;
	available: boolean;
	// Switches the dialog to the embedded booking widget (BmsBookingFrame).
	// The dialog owns that switch, since it changes the dialog's title, width
	// and footer, none of which the panel can reach.
	onBook: () => void;
}

// Content of the appointments modal (see Header/BmsAppointmentButton).
//
// Shows the endcustomer's booked appointments and the Calenso link side by
// side rather than one or the other: an endcustomer typically ends up with at
// least two appointments, so "book another" stays reachable once the list is
// non-empty.
//
// Both of BMS's links are offered here, and the distinction matters: each row's
// own `appointmentLink` manages THAT appointment (move, cancel), while the
// booking link below the list only ever creates a further one. That is why the
// booking button sits under the list inside the panel rather than in the
// dialog's footer — the two kinds of action read as one group, and a footer
// button would look like it acted on the list as a whole.
//
// Refresh is the one control that stays with the dialog: it sits on the title
// line, which the panel's children cannot reach.
const BmsAppointmentsPanel: React.FC<BmsAppointmentsPanelProps> = ({
	appointments,
	bookingLink,
	loaded,
	available,
	onBook,
}) => {
	const { t, i18n } = useTranslation();

	const hasAppointments = appointments.length > 0;

	return (
		<StyledPanel>
			{!loaded && <Spinner />}

			{/* A failed live check is not "no appointments": saying so would be
			    a lie the customer might act on. Say the state is unavailable
			    instead — the header's refresh is the way to try again. */}
			{loaded && !available && (
				<Typography variant="p">
					<p>{String(t("app.BmsAppointments.panel.unavailable"))}</p>
				</Typography>
			)}

			{loaded && available && !hasAppointments && (
				<Typography variant="p">
					<p>{String(t("app.BmsAppointments.panel.empty"))}</p>
				</Typography>
			)}

			{loaded && available && hasAppointments && (
				<StyledList>
					{appointments.map((appointment, index) => {
						const range = formatRange(appointment, i18n.language);
						return (
							<StyledItem
								key={
									appointment.appointmentUid ??
									`${appointment.consultantEmail}-${
										appointment.appointmentStart ?? index
									}`
								}
							>
								<StyledDetails>
									<Typography variant="p">
										<StyledWhen>
											{range ??
												String(
													t(
														"app.BmsAppointments.panel.unknownDate",
													),
												)}
										</StyledWhen>
									</Typography>
									{appointment.consultantEmail && (
										<Typography variant="p">
											<StyledConsultant>
												{String(
													t(
														"app.BmsAppointments.panel.consultant",
													),
												)}
												{": "}
												<a
													href={`mailto:${appointment.consultantEmail}`}
												>
													{
														appointment.consultantEmail
													}
												</a>
											</StyledConsultant>
										</Typography>
									)}
								</StyledDetails>
								{/* Only with a link: an appointment BMS sent
								    without one cannot be managed from here, and
								    a button that does nothing is worse than
								    none. */}
								{appointment.appointmentLink && (
									<StyledLinkButton
										inline
										icon={<Icon name="PencilIcon" />}
										onClick={() =>
											openInNewTab(
												appointment.appointmentLink,
											)
										}
									>
										{String(
											t(
												"app.BmsAppointments.panel.manageButton",
											),
										)}
									</StyledLinkButton>
								)}
							</StyledItem>
						);
					})}
				</StyledList>
			)}

			{/* Under the list, and shown with the empty state too — booking the
			    first appointment is the whole point of the empty case. Hidden
			    only when BMS has sent us no booking link at all: they include
			    it solely while the endcustomer has no appointment, so the
			    server falls back to the copy cached on the project, and both
			    being empty means booking genuinely is not on offer.

			    §8's action row: right-aligned, purple fill. `inline` for auto
			    width, since the default hds Button spans its container. */}
			{loaded && available && bookingLink && (
				<StyledActions>
					<StyledLinkButton
						variant="secondary"
						inline
						onClick={onBook}
					>
						{String(
							t(
								hasAppointments
									? "app.BmsAppointments.panel.newButton"
									: "app.BmsAppointments.panel.bookButton",
							),
						)}
					</StyledLinkButton>
				</StyledActions>
			)}
		</StyledPanel>
	);
};

const StyledPanel = styled.div`
	display: flex;
	flex-direction: column;
	gap: var(--space-3);
`;

const StyledList = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: var(--space-2);
`;

// Details left, the appointment's own action right. Wraps rather than
// squeezing the button: the date line is long in German and the dialog is not
// wide on a phone.
const StyledItem = styled.li`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: var(--space-3);
	border: 1px solid var(--color-border);
	border-radius: 6px;
	padding: var(--space-2) var(--space-3);

	p {
		margin: 0;
	}
`;

const StyledDetails = styled.div`
	/* Takes the free space so the action button stays hard right. */
	flex: 1 1 auto;
	min-width: 0;
`;

const StyledActions = styled.div`
	display: flex;
	justify-content: flex-end;
`;

// Both glyphs here still draw from the legacy set, where the ink covers almost
// the whole box against an outline glyph's half — at the shared 24px the pencil
// read heavier than the label beside it. The slot keeps its 24px, so spacing
// and alignment are untouched; only the glyph drops to --size-icon-sm.
//
// Not --size-icon-sm-legacy: that token compensates for legacy ink so a filled
// glyph reads like a 16px outline one, and at 12px this pencil came out visibly
// smaller than the label rather than merely lighter. The plain 16px is the step
// that looks right on a 40px button here.
//
// A rule rather than Icon's `size` prop, and a child selector: iconSlot sets
// `svg { width: 100% }` on descendants of .hds-btn__icon, which outranks
// anything the Icon sets on itself — the same reason hds/Input's ClearButton
// does it this way.
const StyledLinkButton = styled(Button)`
	.hds-btn__icon > svg {
		width: var(--size-icon-sm);
		height: var(--size-icon-sm);
	}
`;

const StyledWhen = styled.p`
	font-weight: 600;
`;

const StyledConsultant = styled.p`
	opacity: 0.8;
`;

export default BmsAppointmentsPanel;
